import _ from 'lodash';
import * as ApiRequests from '../../api/requests';
import { AuthHelper } from '../../types/auth-helper-types';
import { Role } from '../../types/user';
import { DomainResponse, makeOkDomainResponse } from './domain-response';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getUserList = async (authHelper: AuthHelper, siteId: string) => {

    const userResult = await ApiRequests.userList({ siteId: siteId }, { authHelper });
    if (!userResult.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(userResult.response));
        throw new Error(ApiRequests.extractResponseNotOkMessage(userResult.response));
    }

    return userResult.response.data.users;
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getUserInfo = async (authHelper: AuthHelper, userId: string) => {
    const userResult = await ApiRequests.getUserSettings({ userId: userId }, { authHelper });

    if (!userResult.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(userResult.response));
        throw new Error(ApiRequests.extractResponseNotOkMessage(userResult.response));
    }
    return userResult.response.data;
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getRegistrationRoles = async (authHelper: AuthHelper, userId: string, siteId: string) => {
    const registrationRolesResult = await ApiRequests.userRegistrationRole(
        { userId: userId, siteId: siteId },
        { authHelper }
    );

    if (!registrationRolesResult.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(registrationRolesResult.response));
        throw new Error(ApiRequests.extractResponseNotOkMessage(registrationRolesResult.response));
    }

    return registrationRolesResult.response.data.roles;
};
type GetUserRoleResult = DomainResponse<
    Array<{
        roleId: string;
        siteId: string[];
        roleName: string;
        roleDescription: string;
    }>
>;
type GetUserRoleOperationResult = DomainResponse<{
    roleId: string;
    operations: string[];
}>;
const getUserRoles = async (
    authHelper: AuthHelper,
    userId: string
): Promise<GetUserRoleResult> => {

    const result = await ApiRequests.getUserRole({ userId }, { authHelper });
    if (result.ok) {
        return {
            ok: true,
            value: result.response.data.roles,
        };
    }
    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }

    return {
        ok: false,
        message: ApiRequests.extractErrorCodeWithMessage(result.response),
    };
};

const getUserRoleOperations = async (
    authHelper: AuthHelper,
    roleId: string,
): Promise<GetUserRoleOperationResult> => {
    const result = await ApiRequests.getUserRoleOperation({ roleId }, { authHelper });
    if (result.ok) {
        return {
            ok: true,
            value: result.response.data,
        };
    }
    if (!result.ok) {
        console.error(ApiRequests.extractErrorCodeWithMessage(result.response));
    }

    return {
        ok: false,
        message: ApiRequests.extractErrorCodeWithMessage(result.response),
    };
};

const loadUserRoleWithOperation = async (
    authHelper: AuthHelper,
    userId: string | undefined
): Promise<DomainResponse<Role>> => {
    const roleResult = await getUserRoles(authHelper, userId || '');

    if (!roleResult.ok) {
        return roleResult;
    }

    const roleWithOperationList = await Promise.all(
        _.flatten(
            roleResult.value.map(async (role) => {
                const roleOperationResult = await getUserRoleOperations(authHelper, role.roleId);
                return {
                    roleId: role.roleId,
                    siteId: role.siteId,
                    roleName: role.roleName,
                    roleDescription: role.roleDescription,
                    operations: roleOperationResult.ok ? roleOperationResult.value.operations : undefined,
                };
            })
        )
    );

    return makeOkDomainResponse(roleWithOperationList);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getExistingUserRoles = async (authHelper: AuthHelper, userId: string) => {
    const userRolesResult = await ApiRequests.getUserRole({ userId: userId }, { authHelper });

    if (!userRolesResult.ok) {
        console.error(ApiRequests.extractResponseNotOkMessage(userRolesResult.response));
        throw new Error(ApiRequests.extractResponseNotOkMessage(userRolesResult.response));
    }
    return userRolesResult.response.data;
};

export {
    getUserList,
    getRegistrationRoles,
    loadUserRoleWithOperation,
    getUserInfo,
    getExistingUserRoles,
};
